import { useLazyQuery } from "@apollo/client";
import { AppContext } from "App";
import Button from "components/base/Button";
import { Text } from "components/base/Form";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import ComplianceCertificatesSelector from "components/shared/ComplianceCertificatesSelector";
import LabTestReportsSelector from "components/shared/LabTestReportsSelector";
import { useModals } from "ModalProvider";
import { useContext } from "react";
import { FETCH_GTM_SOURCE_PRODUCT_IMPORT_DATA } from "../graphql";
import { GTMProjectContext } from "../GTMProjectContext";
import useSaveGTMProject from "../useSaveGTMProject";

const ComplianceView = () => {
  const { user } = useContext(AppContext);
  const { originalGTMProject, saveProject, saveProjectRes, autoSave } =
    useContext(GTMProjectContext);

  const [fetchSourceProduct, fetchSourceProductRes] = useLazyQuery(
    FETCH_GTM_SOURCE_PRODUCT_IMPORT_DATA,
    { fetchPolicy: "network-only" },
  );

  const { state, onChange, setValues, loading } = useSaveGTMProject(
    originalGTMProject.id,
    "complianceData",
    JSON.parse(originalGTMProject.complianceData || "{}"),
    1000,
  );
  const modal = useModals();

  const certificates = state.certificates || [];
  const testReports = state.testReports || [];

  function importSourceProductInfoHandler() {
    if (!originalGTMProject.sourceProduct?.id) {
      Alert("error", "Please choose a source product first.");
      return;
    }
    const sourceProductLabel = `[${originalGTMProject.sourceProduct.number}] ${originalGTMProject.sourceProduct.name}`;
    if (
      !window.confirm(
        `Are you sure you want to import source product compliance info?\n\nSource Product: ${sourceProductLabel}`,
      )
    ) {
      return;
    }

    fetchSourceProduct({
      variables: { id: originalGTMProject.sourceProduct.id },
    }).then((res) => {
      const sourceProduct = res.data?.product;
      if (!sourceProduct) {
        Alert("error", "Source product not found.");
        return;
      }
      const importedData = {
        ...state,
        certificates: sourceProduct.complianceCertificates || [],
        testReports: sourceProduct.labTestReports || [],
      };
      setValues(importedData);
      autoSave({
        id: originalGTMProject.id,
        input: {
          complianceData: JSON.stringify(importedData, " ", 2),
        },
      });
      Alert("success", "Source product compliance imported.");
    });
  }

  function removeCertificate(certificateId) {
    if (window.confirm("Are you sure you want to delete this certificate?")) {
      onChange(
        "certificates",
        certificates.filter((c) => c.id !== certificateId),
        false,
      );
    }
  }

  function removeTestReport(reportId) {
    if (window.confirm("Are you sure you want to delete this test report?")) {
      onChange(
        "testReports",
        testReports.filter((r) => r.id !== reportId),
        false,
      );
    }
  }

  function confirmHandler() {
    if (
      window.confirm(
        "Are you sure you want to confirm the compliance information for this project?",
      )
    ) {
      saveProject({
        variables: {
          id: originalGTMProject.id,
          complianceConfirmedAt: new Date().toISOString(),
        },
      });
    }
  }

  const disabled =
    user.email !== "charles@waboba.com" &&
    !!originalGTMProject.complianceConfirmedAt;

  return (
    <div className="card">
      <div className="flex justify-between">
        <h5>
          Compliance (<b className="text-orange-500">Stella</b>)
        </h5>
        <div className="flex items-center space-x-3">
          <Button
            onClick={importSourceProductInfoHandler}
            loading={fetchSourceProductRes.loading}
            disabled={
              !originalGTMProject.sourceProduct?.id ||
              disabled ||
              originalGTMProject.state !== "PACKAGING"
            }
          >
            Import Source Product Info
          </Button>
          {loading && <InlineSpinner size={18} />}
        </div>
      </div>
      {originalGTMProject.complianceConfirmedAt && (
        <div className="text-green-600 font-bold mt-1">
          🟢 Confirmed at{" "}
          {new Date(originalGTMProject.complianceConfirmedAt).toLocaleString()}.
        </div>
      )}

      <div className="mt-4 space-y-6">
        <div className="space-y-3">
          <label>Certificates</label>
          {certificates.length > 0 ? (
            <div className="-mx-2">
              <table className="with-line">
                <tbody>
                  {certificates.map((certificate) => (
                    <tr key={certificate.id}>
                      <td>
                        <div className="flex items-center justify-between">
                          <div>
                            {certificate.certificateNumber ? (
                              <div className="text-xs opacity-70">
                                #{certificate.certificateNumber}
                              </div>
                            ) : null}
                            <a
                              className="link"
                              href={`/certificates/${certificate.id}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {certificate.title || "-"}{" "}
                              {certificate.certificateType
                                ? `(${certificate.certificateType})`
                                : ""}
                            </a>
                          </div>
                          {!disabled && (
                            <Button
                              trash
                              onClick={() => removeCertificate(certificate.id)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs opacity-60">No certificates selected.</div>
          )}

          {!disabled && (
            <Button
              onClick={() =>
                modal.present({
                  title: "Choose Certificates",
                  center: true,
                  children: (
                    <ComplianceCertificatesSelector
                      defaultSelectedCertificates={certificates}
                      onDone={(selectedCertificates) => {
                        onChange("certificates", selectedCertificates, false);
                        modal.hide();
                      }}
                    />
                  ),
                })
              }
            >
              + Choose Certificates
            </Button>
          )}
        </div>

        <div className="space-y-3">
          <label>Lab Test Reports</label>
          {testReports.length > 0 ? (
            <div className="-mx-2">
              <table className="with-line">
                <tbody>
                  {testReports.map((report) => (
                    <tr key={report.id}>
                      <td>
                        <div className="flex items-center justify-between">
                          <div>
                            {report.reportNumber ? (
                              <div className="text-xs opacity-70">
                                #{report.reportNumber}
                              </div>
                            ) : null}
                            <a
                              className="link"
                              href={`/test-reports/${report.id}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {report.title || "-"}
                            </a>
                          </div>
                          {!disabled && (
                            <Button
                              trash
                              onClick={() => removeTestReport(report.id)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs opacity-60">No test reports selected.</div>
          )}

          {!disabled && (
            <Button
              onClick={() =>
                modal.present({
                  title: "Choose Test Reports",
                  center: true,
                  children: (
                    <LabTestReportsSelector
                      defaultSelectedTestReports={testReports}
                      onDone={(selectedTestReports) => {
                        onChange("testReports", selectedTestReports, false);
                        modal.hide();
                      }}
                    />
                  ),
                })
              }
            >
              + Choose Test Reports
            </Button>
          )}
        </div>

        <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-700">
          <label>Compliance Notes</label>
          {disabled ? (
            <div className="text-sm">{state.complianceRemark}</div>
          ) : (
            <Text
              value={state.complianceRemark}
              onChange={(e) => onChange("complianceRemark", e.target.value)}
            />
          )}
        </div>
      </div>

      {!originalGTMProject.complianceConfirmedAt && (
        <div className="mt-4 flex justify-end">
          <Button onClick={confirmHandler} loading={saveProjectRes.loading}>
            Confirm Compliance Information
          </Button>
        </div>
      )}
    </div>
  );
};

export default ComplianceView;
